import React, {useEffect, useMemo, useState} from 'react'
import {
  SortingState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import moment from 'moment'
import {Modal} from 'react-bootstrap'
import {useNavigate} from 'react-router-dom'
import {ToastContainer} from 'react-toastify'
import {KTCard, KTCardBody, KTIcon} from '../../../_websoft/helpers'
import {
  deleteObradaSmjene,
  getObradaSmjene,
  getSmjenaProizvelaPakete,
} from '../../api/thermoboardNalozi'
import {TableActionButton} from '../../components/TableActionBtn/TableActionButton'
import ExportButton from '../../components/TableExportToExcel/ExportButton'
import LoadingComponent from '../../components/LoadingComponent/LoadingComponent'
import TablePagination from '../../components/TablePagination/TablePagination'
import TableSearch from '../../components/TableSearch/TableSearch'
import notifyToast from '../../components/Toast/Toast'
import {PaginationModel} from '../../models/PaginationModel'

type ObradaSmjeneUposlenik = {
  id?: number
  smjena_id?: number
  UposlenikImePrezime?: string | null
  uposlenik?: {
    id?: number
    UposlenikImePrezime?: string | null
  } | null
}

type ObradaSmjeneRow = {
  id: number
  vrijeme_pocetka?: string | null
  vrijeme_zavrsetka?: string | null
  efektivno_vrijeme?: string | number | null
  norma_vrijeme?: string | number | null
  ucinak?: string | number | null
  u_normi?: boolean | number | string | null
  broj_paketa?: number | string | null
  pauza?: number | string | null
  pauza_minuta?: number | string | null
  pauza_kvar?: number | string | null
  pauza_kvar_odobreno?: number | string | null
  smjena_id?: number | string | null
  uposlenik?: {
    id?: number
    UposlenikImePrezime?: string | null
  } | null
  uposlenici?: ObradaSmjeneUposlenik[] | null
}

type ObradaSmjeneParams = {
  page: number
  per_page: number
}

type SmjenaPaket = {
  Id: number
  VrstaPakovanja?: string | null
  Duzina?: number | string | null
  Sirina?: number | string | null
  Komada?: number | string | null
  ProizvedenDatum?: string | null
  tip_panela?: {
    Naziv?: string | null
  } | null
  proizveo_uposlenik?: {
    UposlenikImePrezime?: string | null
  } | null
}

const getResponseRows = (response: any): ObradaSmjeneRow[] => {
  const responseData = response?.data

  if (Array.isArray(responseData)) {
    return responseData
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data
  }

  if (Array.isArray(responseData?.data?.data)) {
    return responseData.data.data
  }

  return []
}

const getPaginationData = (response: any) => {
  const responseData = response?.data

  if (responseData?.current_page || responseData?.per_page) {
    return responseData
  }

  if (responseData?.data?.current_page || responseData?.data?.per_page) {
    return responseData.data
  }

  return null
}

const getPackageRows = (response: any): SmjenaPaket[] => {
  const responseData = response?.data

  if (Array.isArray(responseData)) {
    return responseData
  }

  if (Array.isArray(responseData?.data)) {
    return responseData.data
  }

  if (Array.isArray(responseData?.data?.data)) {
    return responseData.data.data
  }

  return []
}

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '-'
  }

  return moment(value).isValid() ? moment(value).format('DD.MM.YYYY HH:mm') : value
}

const getUposlenikName = (item?: ObradaSmjeneUposlenik | null) =>
  item?.uposlenik?.UposlenikImePrezime || item?.UposlenikImePrezime || '-'

const TherboardObradaPloca: React.FC = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<ObradaSmjeneRow[]>([])
  const [loading, setLoading] = useState(true)
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [selectedSmjena, setSelectedSmjena] = useState<ObradaSmjeneRow | null>(null)
  const [selectedPaketiSmjena, setSelectedPaketiSmjena] = useState<ObradaSmjeneRow | null>(null)
  const [paketi, setPaketi] = useState<SmjenaPaket[]>([])
  const [paketiLoading, setPaketiLoading] = useState(false)
  const [params, setParams] = useState<ObradaSmjeneParams>({
    page: 1,
    per_page: 10,
  })
  const [pagination, setPagination] = useState<PaginationModel>({
    curPage: 1,
    totalPage: 1,
    perPage: 10,
  })

  const loadData = async () => {
    setLoading(true)

    try {
      const response = await getObradaSmjene(params)
      const rows = getResponseRows(response)
      const paginationData = getPaginationData(response)
      const perPage = Number(paginationData?.per_page ?? params.per_page)
      const total = Number(paginationData?.total ?? rows.length)

      setData(rows)
      setPagination({
        curPage: Number(paginationData?.current_page ?? params.page),
        totalPage: Math.max(Math.ceil(total / perPage), 1),
        perPage,
      })
    } catch (error: any) {
      notifyToast(
        false,
        error?.response?.data?.message || error?.message || 'Nije moguće učitati obradu smjena.'
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadData()
  }, [params.page, params.per_page])

  const handleDelete = async (id: number) => {
    try {
      const response = await deleteObradaSmjene(id)
      notifyToast(true, response?.message || 'Smjena je uspješno obrisana.')
      loadData()
    } catch (error: any) {
      notifyToast(
        false,
        error?.response?.data?.message || error?.message || 'Smjena nije obrisana.'
      )
    }
  }

  const openPaketi = async (smjena: ObradaSmjeneRow) => {
    setSelectedPaketiSmjena(smjena)
    setPaketi([])
    setPaketiLoading(true)

    try {
      const response = await getSmjenaProizvelaPakete(smjena.id)
      setPaketi(getPackageRows(response))
    } catch (error: any) {
      notifyToast(
        false,
        error?.response?.data?.message || error?.message || 'Nije moguće učitati pakete.'
      )
    } finally {
      setPaketiLoading(false)
    }
  }

  const columns = useMemo(
    () => [
      {
        id: 'id',
        header: 'Id',
        accessorKey: 'id',
      },
      {
        id: 'vrijeme_pocetka',
        header: 'Početak',
        accessorFn: (row: ObradaSmjeneRow) => formatDateTime(row.vrijeme_pocetka),
      },
      {
        id: 'vrijeme_zavrsetka',
        header: 'Kraj',
        accessorFn: (row: ObradaSmjeneRow) => formatDateTime(row.vrijeme_zavrsetka),
      },
      {
        id: 'efektivno_vrijeme',
        header: 'Efek. vrijeme',
        accessorFn: (row: ObradaSmjeneRow) => row.efektivno_vrijeme ?? '-',
      },
      {
        id: 'norma_vrijeme',
        header: 'Norma vrijeme',
        accessorFn: (row: ObradaSmjeneRow) => row.norma_vrijeme ?? '-',
      },
      {
        id: 'ucinak',
        header: 'Učinak',
        accessorFn: (row: ObradaSmjeneRow) => row.ucinak ?? '-',
      },
      {
        id: 'u_normi',
        header: 'U normi',
        accessorFn: (row: ObradaSmjeneRow) => row.u_normi,
        cell: ({row}: any) => {
          const isInNorm =
            row.original.u_normi === true ||
            row.original.u_normi === 1 ||
            row.original.u_normi === '1'

          return isInNorm ? (
            <KTIcon iconName='check-circle' className='fs-2 text-success' />
          ) : (
            <KTIcon iconName='cross-circle' className='fs-2 text-danger' />
          )
        },
      },
      {
        id: 'pauza',
        header: 'Pauza',
        accessorFn: (row: ObradaSmjeneRow) => row.pauza ?? row.pauza_minuta ?? '-',
      },
      {
        id: 'pauza_kvar',
        header: 'Zastoj',
        accessorFn: (row: ObradaSmjeneRow) => row.pauza_kvar ?? '-',
        cell: ({row}: any) => {
          const status = row.original.pauza_kvar_odobreno

          if (status === null || status === undefined || status === '') {
            return <span className='badge badge-light-warning'>Potrebna akcija</span>
          }

          if (Number(status) === 1) {
            return (
              <span className='d-inline-flex align-items-center gap-2 text-success fw-bold'>
                <KTIcon iconName='check-circle' className='fs-2 text-success' />
                {row.original.pauza_kvar ?? '-'}
              </span>
            )
          }

          return (
            <span className='d-inline-flex align-items-center gap-2 text-danger fw-bold'>
              <KTIcon iconName='cross-circle' className='fs-2 text-danger' />
              {row.original.pauza_kvar ?? '-'}
            </span>
          )
        },
      },
      {
        id: 'vodja_smjene',
        header: 'Vođa smjene',
        accessorFn: (row: ObradaSmjeneRow) =>
          row.uposlenik?.UposlenikImePrezime || '-',
      },
      {
        id: 'smjena',
        header: 'Smjena',
        accessorFn: (row: ObradaSmjeneRow) => row.smjena_id ?? '-',
        cell: ({row}: any) => (
          <button
            type='button'
            className='btn btn-sm btn-light-primary fw-bold'
            onClick={() => setSelectedSmjena(row.original)}
          >
            <KTIcon iconName='people' className='fs-3' />
            Smjena {row.original.smjena_id ?? ' '}
          </button>
        ),
      },
      {
        id: 'paketi',
        header: 'Paketi',
        enableSorting: false,
        cell: ({row}: any) => (
          <button
            type='button'
            className='btn btn-sm btn-light-success fw-bold'
            onClick={() => openPaketi(row.original)}
          >
            <KTIcon iconName='package' className='fs-3' />
            Paketi ({row.original.broj_paketa ?? 0})
          </button>
        ),
      },
      {
        id: 'akcija',
        header: 'Akcija',
        enableSorting: false,
        cell: ({row}: any) => (
          <TableActionButton
            onEdit={() => navigate(`/thermoboard/obrada-ploca/izmjena/${row.original.id}`)}
            onDelete={() => handleDelete(row.original.id)}
          />
        ),
      },
    ],
    [navigate]
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    state: {
      sorting,
      globalFilter,
    },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
  })

  return (
    <KTCard>
      <ToastContainer />

      <Modal show={Boolean(selectedSmjena)} onHide={() => setSelectedSmjena(null)} centered size='lg'>
        <Modal.Header closeButton>
          <Modal.Title>Smjena {selectedSmjena?.smjena_id ?? '-'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(selectedSmjena?.uposlenici?.length ?? 0) > 0 ? (
            <div className='table-responsive'>
              <table className='table table-row-bordered align-middle mb-0'>
                <thead>
                  <tr className='fw-bold text-gray-700 bg-light'>
                    <th className='ps-4'>Smjena ID</th>
                    <th>Uposlenik</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedSmjena?.uposlenici?.map((item, index) => (
                    <tr key={`${item.id ?? item.uposlenik?.id ?? 'uposlenik'}-${index}`}>
                      <td className='ps-4'>{item.smjena_id ?? selectedSmjena.smjena_id ?? '-'}</td>
                      <td className='fw-semibold'>{getUposlenikName(item)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className='text-muted fw-semibold py-4'>Nema uposlenika za ovu smjenu.</div>
          )}
        </Modal.Body>
      </Modal>

      <Modal
        show={Boolean(selectedPaketiSmjena)}
        onHide={() => setSelectedPaketiSmjena(null)}
        centered
        size='xl'
      >
        <Modal.Header closeButton>
          <Modal.Title>Paketi proizvedeni u smjeni #{selectedPaketiSmjena?.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {paketiLoading ? (
            <LoadingComponent />
          ) : (
            <div className='table-responsive'>
              <table className='table table-row-bordered table-striped align-middle mb-0'>
                <thead>
                  <tr className='fw-bold text-gray-700 bg-light'>
                    <th className='ps-4'>Id</th>
                    <th>Tip panela</th>
                    <th>Vrsta pakovanja</th>
                    <th>Dužina</th>
                    <th>Širina</th>
                    <th>Komada</th>
                    <th>Proizveo</th>
                    <th>Proizveden datum</th>
                  </tr>
                </thead>
                <tbody>
                  {paketi.length > 0 ? (
                    paketi.map((paket) => (
                      <tr key={paket.Id}>
                        <td className='ps-4 fw-bold'>{paket.Id}</td>
                        <td>{paket.tip_panela?.Naziv || '-'}</td>
                        <td>{paket.VrstaPakovanja || '-'}</td>
                        <td>{paket.Duzina ?? '-'}</td>
                        <td>{paket.Sirina ?? '-'}</td>
                        <td>{paket.Komada ?? '-'}</td>
                        <td>{paket.proizveo_uposlenik?.UposlenikImePrezime || '-'}</td>
                        <td>
                          {paket.ProizvedenDatum
                            ? moment(paket.ProizvedenDatum).format('DD-MM-YYYY HH:mm:ss')
                            : '-'}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={8} className='text-center py-8 text-muted fw-semibold'>
                        Nema proizvedenih paketa za ovu smjenu.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Modal.Body>
      </Modal>

      <KTCardBody className='py-6'>
        <div className='d-flex flex-column flex-md-row justify-content-between align-items-md-end gap-4 mb-5'>
          <div>
            <label className='form-label fw-bold fs-6'>Traži</label>
            <TableSearch onSearch={(text: string) => setGlobalFilter(text)} />
          </div>

          <ExportButton table={table} filename='thermoboard_obrada_smjena' />
        </div>

        {loading ? (
          <LoadingComponent />
        ) : (
          <div className='table-responsive border rounded'>
            <table className='table table-row-bordered table-row-gray-200 align-middle gy-4 mb-0'>
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr
                    key={headerGroup.id}
                    className='text-start fw-bolder fs-7 text-uppercase bg-dark'
                  >
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className='text-white ps-4'
                        onClick={header.column.getToggleSortingHandler()}
                        style={{
                          cursor: header.column.getCanSort() ? 'pointer' : 'default',
                        }}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {{asc: ' ↑', desc: ' ↓'}[header.column.getIsSorted() as string] ?? ''}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody className='text-gray-700'>
                {table.getRowModel().rows.length > 0 ? (
                  table.getRowModel().rows.map((row) => (
                    <tr key={row.id}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className='ps-4'>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columns.length} className='text-center py-8 text-muted fw-semibold'>
                      Nema pronađenih smjena.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <TablePagination
          updatePage={(page: number, perPage: number) =>
            setParams({
              page,
              per_page: perPage,
            })
          }
          pagination={pagination}
          isLoading={loading}
        />
      </KTCardBody>
    </KTCard>
  )
}

export default TherboardObradaPloca
